/**
 * 결제 API 훅
 *
 * 결제 생성, 상태 조회, 폴링 등 결제 관련 API 호출을 관리합니다.
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { paymentApi, tableSessionApi } from '@/lib/api/payments';
import type { TableSessionPaymentRequestMethod } from '@/lib/api/payments';
import type {
  ChargePaymentRequest,
  ChargePaymentResponse,
  ChargeSessionRequest,
  ChargeSessionResponse,
  Payment,
  PaymentMethod,
  PaymentStatus,
  PaymentStatusResponse,
} from '@/lib/api/types';

export interface UsePaymentApiOptions {
  /**
   * 폴링 간격 (ms) - 기본: 3000ms
   */
  pollingInterval?: number;

  /**
   * 폴링 최대 횟수 - 기본: 60 (약 3분)
   */
  maxPollingAttempts?: number;

  /**
   * 비회원 비밀번호
   */
  guestPassword?: string;
  /** 지정 시 세션 결제 API를 이 Bill로 제한한다. */
  billId?: number;
  /** Bill 소유 참여자 인증. 원 주문의 guest_password를 대신한다. */
  comensalDeviceToken?: string;
  expectedBillSubtotalMinor?: number;
  expectedTotalMinor?: number;
}

export interface UsePaymentApiReturn {
  /**
   * 결제 생성
   */
  createPayment: (orderId: number, method: PaymentMethod, pointsToUse?: number) => Promise<{
    payment: Payment;
    gateway?: 'bloomin';
    bloomin?: {
      requires_card_input: boolean;
      initiate_url?: string;
      challenge_url?: string | null;
      reference3D?: string | null;
      transaccionId?: string | null;
    };
  }>;

  /**
   * 후불 "지금 결제" 카드 결제 (C3 #590)
   *
   * 후불 매장에서 이미 생성된 pending 결제에 카드 정보를 제출.
   * 정규화된 응답(`bloomin.challengeUrl`/`reference3D`)을 반환한다.
   */
  chargePayment: (
    orderId: number,
    paymentId: number,
    cardData: ChargePaymentRequest
  ) => Promise<ChargePaymentResponse>;

  /**
   * 후불 세션 합산청구 (#1705 R8)
   *
   * 테이블 세션 미결제 후불 주문 N건을 1회 Bloomin 3DS 로 합산 청구.
   * 정규화된 응답(`bloomin.challengeUrl`/`reference3D`)을 반환한다.
   */
  chargeSession: (
    token: string,
    cardData: ChargeSessionRequest
  ) => Promise<ChargeSessionResponse>;

  /** 테이블 세션 현장 결제 요청을 POS에 전송한다. */
  requestPaymentRequest: (
    token: string,
    method: TableSessionPaymentRequestMethod,
    tipAmount?: number,
  ) => Promise<unknown>;

  /** 테이블 세션 현금 결제 요청을 POS에 전송한다. */
  requestCashPayment: (token: string, tipAmount?: number) => Promise<unknown>;

  /**
   * 결제 상태 조회
   */
  getPaymentStatus: (
    orderId: number,
    paymentId: number
  ) => Promise<PaymentStatusResponse>;

  /**
   * 결제 상태 폴링 시작
   */
  startPolling: (
    orderId: number,
    paymentId: number,
    onStatusChange?: (status: PaymentStatusResponse) => void,
    onTimeout?: () => void
  ) => void;

  /**
   * 결제 상태 폴링 중지
   */
  stopPolling: () => void;

  /**
   * 결제 취소
   */
  cancelPayment: (
    orderId: number,
    paymentId: number,
    reason?: string
  ) => Promise<Payment>;

  /**
   * 현재 결제 정보
   */
  payment: Payment | null;

  /**
   * 현재 결제 상태
   */
  status: PaymentStatus | null;

  /**
   * 로딩 상태
   */
  isLoading: boolean;

  /**
   * 폴링 상태
   */
  isPolling: boolean;

  /**
   * 폴링 타임아웃 여부
   */
  isTimedOut: boolean;

  /**
   * 현재 폴링 횟수
   */
  pollingCount: number;

  /**
   * 에러 메시지
   */
  error: string | null;

  /**
   * 에러 초기화
   */
  clearError: () => void;
}

export function usePaymentApi(
  options: UsePaymentApiOptions = {}
): UsePaymentApiReturn {
  const {
    pollingInterval = 3000,
    maxPollingAttempts = 60,
    guestPassword,
    billId,
    comensalDeviceToken,
    expectedBillSubtotalMinor,
    expectedTotalMinor,
  } = options;

  const [payment, setPayment] = useState<Payment | null>(null);
  const [status, setStatus] = useState<PaymentStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [isTimedOut, setIsTimedOut] = useState(false);
  const [pollingCount, setPollingCount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const pollingCountRef = useRef(0);
  const isMountedRef = useRef(true);

  // 클린업 - 언마운트 시 폴링 중지 및 비동기 상태 업데이트 방지
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, []);

  /**
   * 결제 생성
   *
   * 비회원 결제 시 sessionStorage에 비밀번호를 저장합니다.
   * PaymentResultPage에서 이 값을 사용하여 결제 상태를 조회합니다.
   */
  const createPayment = useCallback(
    async (orderId: number, method: PaymentMethod, pointsToUse?: number) => {
      setIsLoading(true);
      setError(null);

      try {
        // #1: 포인트 결제 지원 시 points_amount 전송
        // 백엔드 미지원 시 400/422 에러가 발생하며, 에러 메시지를 사용자에게 표시
        const paymentData: { method: PaymentMethod; guest_password?: string; points_amount?: number } = {
          method,
          guest_password: guestPassword,
        };

        // 포인트가 0보다 크면 points_amount 전송 (백엔드 미지원 시 에러 처리됨)
        if (pointsToUse && pointsToUse > 0) {
          paymentData.points_amount = pointsToUse;
        }

        const response = await paymentApi.createPayment(orderId, paymentData);

        setPayment(response.data);
        setStatus(response.data.status);

        // 비회원 결제 시 sessionStorage에 비밀번호 저장 (URL 노출 방지)
        // PaymentResultPage에서 결제 완료 후 상태 조회에 사용
        if (guestPassword) {
          sessionStorage.setItem(`payment_guest_${orderId}`, guestPassword);
        }

        return {
          payment: response.data,
          gateway: response.gateway,
          bloomin: response.bloomin,
        };
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to create payment';
        setError(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [guestPassword]
  );

  /**
   * 후불 "지금 결제" 카드 결제 (C3 #590)
   *
   * createPayment 와 동형: 로딩/에러 상태 관리 + 비회원 비밀번호 sessionStorage 저장.
   * 정규화된 응답(`bloomin.challengeUrl`/`reference3D`)을 그대로 반환한다.
   */
  const chargePayment = useCallback(
    async (orderId: number, paymentId: number, cardData: ChargePaymentRequest) => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await paymentApi.chargePayment(orderId, paymentId, {
          ...cardData,
          guest_password: cardData.guest_password ?? guestPassword,
        });

        setPayment(response.payment);
        setStatus(response.payment.status);

        // 비회원 결제 시 sessionStorage 에 비밀번호 저장 (createPayment 와 동일 — URL 노출 방지)
        const effectiveGuestPassword = cardData.guest_password ?? guestPassword;
        if (effectiveGuestPassword) {
          sessionStorage.setItem(`payment_guest_${orderId}`, effectiveGuestPassword);
        }

        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to charge payment';
        setError(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [guestPassword]
  );

  /**
   * 후불 세션 합산청구 (#1705 R8)
   *
   * chargePayment 와 동형: 로딩/에러 상태 관리 + 비회원 비밀번호 body 동봉.
   * 정규화된 응답(`bloomin.challengeUrl`/`reference3D`)을 그대로 반환한다.
   * 번들은 payment 단건이 아니므로 payment/status 상태는 변경하지 않는다(challenge redirect 가 정상 흐름).
   */
  const chargeSession = useCallback(
    async (token: string, cardData: ChargeSessionRequest) => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await tableSessionApi.chargeSession(token, {
          ...cardData,
          bill_id: cardData.bill_id ?? billId,
          expected_bill_subtotal_minor:
            cardData.expected_bill_subtotal_minor ?? expectedBillSubtotalMinor,
          expected_total_minor: cardData.expected_total_minor ?? expectedTotalMinor,
          // Bill 범위는 Comensal 토큰이 주체다. 대표 주문의 비밀번호를 요구하지 않는다.
          guest_password:
            (cardData.bill_id ?? billId) === undefined && comensalDeviceToken === undefined
              ? (cardData.guest_password ?? guestPassword)
              : undefined,
        }, comensalDeviceToken);

        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to charge session';
        setError(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [
      guestPassword,
      billId,
      comensalDeviceToken,
      expectedBillSubtotalMinor,
      expectedTotalMinor,
    ]
  );

  // guestPassword 를 의존성에 넣는다 — admin #2231 이 이 엔드포인트에 인가를 붙였고,
  // 비회원은 증명 없이는 403 이다. 회원 모드에서는 undefined 라 body 에 실리지 않는다.
  const requestCashPayment = useCallback(
    async (token: string, tipAmount?: number) => tableSessionApi.requestCashPayment(
      token,
      billId === undefined && comensalDeviceToken === undefined ? guestPassword : undefined,
      {
        billId,
        comensalDeviceToken,
        expectedBillSubtotalMinor,
        expectedTotalMinor,
        tipAmount: billId === undefined ? undefined : tipAmount,
      },
    ),
    [
      guestPassword,
      billId,
      comensalDeviceToken,
      expectedBillSubtotalMinor,
      expectedTotalMinor,
    ]
  );

  const requestPaymentRequest = useCallback(
    async (token: string, method: TableSessionPaymentRequestMethod, tipAmount?: number) =>
      tableSessionApi.requestPaymentRequest(
        token,
        method,
        billId === undefined && comensalDeviceToken === undefined ? guestPassword : undefined,
        {
          billId,
          comensalDeviceToken,
          expectedBillSubtotalMinor,
          expectedTotalMinor,
          tipAmount: billId === undefined ? undefined : tipAmount,
        },
      ),
    [
      guestPassword,
      billId,
      comensalDeviceToken,
      expectedBillSubtotalMinor,
      expectedTotalMinor,
    ]
  );

  /**
   * 결제 상태 조회
   */
  const getPaymentStatus = useCallback(
    async (orderId: number, paymentId: number) => {
      try {
        const response = await paymentApi.pollStatus(
          orderId,
          paymentId,
          guestPassword
        );

        setStatus(response.status);

        return response;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to get payment status';
        setError(errorMessage);
        throw err;
      }
    },
    [guestPassword]
  );

  /**
   * 결제 상태 폴링 시작
   *
   * 타임아웃 시 onTimeout 콜백이 호출되며, isTimedOut 상태가 true로 설정됩니다.
   */
  const startPolling = useCallback(
    (
      orderId: number,
      paymentId: number,
      onStatusChange?: (status: PaymentStatusResponse) => void,
      onTimeout?: () => void
    ) => {
      // 인라인 stop 함수 (순환 의존성 방지)
      const doStopPolling = () => {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
        setIsPolling(false);
      };

      // 이미 폴링 중이면 중지
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }

      pollingCountRef.current = 0;
      setPollingCount(0);
      setIsPolling(true);
      setIsTimedOut(false);

      const poll = async () => {
        // 언마운트된 경우 폴링 중지
        if (!isMountedRef.current) {
          doStopPolling();
          return;
        }

        pollingCountRef.current++;
        setPollingCount(pollingCountRef.current);

        // 최대 시도 횟수 초과
        if (pollingCountRef.current > maxPollingAttempts) {
          doStopPolling();
          if (isMountedRef.current) {
            setIsTimedOut(true);
            setError('결제 상태 확인 시간이 초과되었습니다. 다시 시도해 주세요.');
          }
          onTimeout?.();
          return;
        }

        try {
          const statusResponse = await paymentApi.pollStatus(
            orderId,
            paymentId,
            guestPassword
          );

          // 언마운트된 경우 상태 업데이트 스킵
          if (!isMountedRef.current) {
            return;
          }

          setStatus(statusResponse.status);
          onStatusChange?.(statusResponse);

          // 최종 상태면 폴링 중지
          if (statusResponse.is_final) {
            doStopPolling();
          }
        } catch (err) {
          console.error('Polling error:', err);
          // 에러 발생해도 폴링 계속 시도 (네트워크 일시적 오류 등)
        }
      };

      // 즉시 첫 번째 조회 실행
      poll();

      // 인터벌 설정
      pollingRef.current = setInterval(poll, pollingInterval);
    },
    [guestPassword, maxPollingAttempts, pollingInterval]
  );

  /**
   * 결제 상태 폴링 중지
   */
  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    setIsPolling(false);
  }, []);

  /**
   * 결제 취소
   */
  const cancelPayment = useCallback(
    async (orderId: number, paymentId: number, reason?: string) => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await paymentApi.cancelPayment(orderId, paymentId, {
          guest_password: guestPassword,
          reason,
        });

        setPayment(response.data);
        setStatus(response.data.status);
        stopPolling();

        return response.data;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to cancel payment';
        setError(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [guestPassword, stopPolling]
  );

  /**
   * 에러 초기화
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    createPayment,
    chargePayment,
    chargeSession,
    requestPaymentRequest,
    requestCashPayment,
    getPaymentStatus,
    startPolling,
    stopPolling,
    cancelPayment,
    payment,
    status,
    isLoading,
    isPolling,
    isTimedOut,
    pollingCount,
    error,
    clearError,
  };
}

export default usePaymentApi;
